import { Comment, GetCommentsCommandOutput, PostCommentCommandOutput } from "@amzn/ito-client";
import { SUPPORT_TAGS } from "common/types";
import { ITOConsumer, ITOOrdersService } from "lib/services";
import { useEffect, useState } from "react";

import { GetCommentsResult } from "./types";

export interface useOrderMessagesResult {
  isLoading: boolean;
  isSubmitting: boolean;
  messages: GetCommentsResult;
  submitComment: (orderTaxId: string, comment: string) => Promise<boolean>;
  hasError: boolean;
}

const DEFAULT_PAGE_SIZE = 100;

export function useOrderMessages(consumer: ITOConsumer, orderTaxId: string): useOrderMessagesResult {
  const ito = new ITOOrdersService(consumer);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<GetCommentsResult>(undefined);
  const [postCommentResponse, setPostCommentResponse] = useState<PostCommentCommandOutput | undefined>(undefined);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    fetchMessages(orderTaxId);
  }, [postCommentResponse]);

  /**
   * Fetches the comments for a given order and updates component state.
   *
   * @async
   * @param {string} orderTaxId - Taxonomy ID of the order to load messages for.
   * @returns {Promise<void>} Resolves after messages are loaded or an error has been handled.
   *
   * Side effects:
   * - Calls ito.GetComments with DEFAULT_PAGE_SIZE to retrieve comments.
   * - Updates `messages` state via setMessages(response).
   * - On error, sets `hasError` to true.
   * - Always sets `isLoading` to false when finished.
   */
  const fetchMessages = async (orderTaxId: string) => {
    try {
      const response: GetCommentsResult = await ito.GetComments(orderTaxId, DEFAULT_PAGE_SIZE);
      setMessages(response);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Posts a new comment for the given order and updates local state.
   *
   * @async
   * @param {string} orderTaxId - Taxonomy ID of the order to which the comment is related to.
   * @param {string} comment - The text content of the comment to submit.
   * @returns {Promise<boolean>}
   * - Resolves to `true` if the comment was successfully posted and local state was updated.
   * - Resolves to `false` if an error occurred during submission.
   *
   * Side effects:
   * - Toggles `isSubmitting` flag before/after the API call.
   * - On error, sets `hasError` to true and resets `isSubmitting`.
   * - Stores the raw API response in `postCommentResponse`.
   * - Appends the new comment to `messages` state to immediately reflect it in the UI.
   */
  const submitComment = async (orderTaxId: string, comment: string): Promise<boolean> => {
    let response: PostCommentCommandOutput | undefined = undefined;
    try {
      setIsSubmitting(true);
      const csrfToken = await ito.GetSecurityToken();
      response = await ito.PostComment(orderTaxId!, comment, csrfToken, SUPPORT_TAGS);
    } catch (error) {
      setIsSubmitting(false);
      setHasError(true);
      return false;
    }
    setPostCommentResponse(response);
    setIsSubmitting(false);

    // Update the comments data to avoid keeping the user waiting for the request
    setMessages((prev: GetCommentsResult) => {
      if (!prev) return null;

      const newMessages: GetCommentsCommandOutput = {
        totalResults: prev.totalResults,
        pageSize: prev.pageSize,
        $metadata: prev.$metadata,
        comments: [...(prev.comments ?? []), response as Comment],
      };
      return newMessages;
    });
    return true;
  };

  return {
    isLoading,
    isSubmitting,
    messages,
    submitComment,
    hasError,
  };
}
